<template>
    <AppModal :title="$t('createProductPurchaseExpenseModal.showButton')" ref="modal" @on-hide="hide" size="full">
        <div class="modal-body">
            <template v-if="fetchTask.state.isRunning">
                <TaskProgressBar :done="fetchTask.state.done" :total="fetchTask.state.total"/>
            </template>
            <template v-else-if="createTask.state.isRunning">
                <TaskProgressBar :done="createTask.state.done" :total="createTask.state.total"/>
            </template>
            <template v-else>
                <div class="table-responsive">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th class="text-truncate" width="100px">{{ $t('table.titles.id') }}</th>
                                <th class="text-truncate" width="78px">{{ $t('table.titles.image') }}</th>
                                <th class="text-truncate" width="100px">{{ $t('table.titles.sku') }}</th>
                                <th class="text-truncate" width="300px">{{ $t('table.titles.name') }}</th>
                                <th class="text-truncate text-right" width="100px">{{ $t('table.titles.amount') }}</th>
                                <th class="text-truncate text-right" width="100px">{{ $t('table.titles.price') }}</th>
                                <th class="text-truncate text-right" width="100px">{{ $t('table.titles.sum') }}</th>
                                <th class="text-truncate" width="150px">{{ $t('table.titles.seller_name') }}</th>
                                <th class="text-truncate text-right" width="150px">{{ $t('table.titles.seller_price') }}</th>
                                <th class="text-truncate text-right" width="150px">{{ $t('table.titles.seller_sum') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="orderProduct in orderProducts" :key="orderProduct.uuid">
                                <td>{{ orderProduct.id }}</td>
                                <td>
                                    <img class="table-image" :src="orderProduct.product.main_image_url || PLACEHOLDER_IMAGE_URL" :alt="orderProduct.product.name"></img>
                                </td>
                                <td>
                                    <AppNullableCell>
                                        <template #default v-if="orderProduct.product.sku">
                                            <span class="name-string" :title="orderProduct.product.sku">{{ orderProduct.product.sku }}</span>
                                        </template>
                                    </AppNullableCell>
                                </td>
                                <td>
                                    <span class="name-string" :title="orderProduct.product.name">{{ orderProduct.product.name }}</span>
                                </td>
                                <td class="text-right">{{ orderProduct.amount }}</td>
                                <td class="text-right">{{ Price.factory(orderProduct.price).toCurrencyString('UAH') }}</td>
                                <td class="text-right">{{ Price.factory(orderProduct.amount * orderProduct.price).toCurrencyString('UAH') }}</td>
                                <td>
                                    <AppAutoComplete
                                        url="/dropshippers/products"
                                        :model-value="orderProduct.seller?.uuid"
                                        :selected-value="orderProduct.seller?.name"
                                        @update:model-value="(dropshipperProduct) => selectDropshipper(orderProduct, dropshipperProduct.dropshipper, dropshipperProduct.price)"
                                        :query-params="{
                                            customer_product_uuids: [orderProduct.product.uuid],
                                            extra_fields: ['dropshipper']
                                        }"
                                        optionKey="uuid"
                                        optionValue="dropshipper.name"
                                    />
                                </td>
                                <td>
                                    <InputGroup>
                                        <Input v-model="orderProduct.seller_price" @update:model-value="updateSellerSum(orderProduct)" :disabled="!orderProduct.seller" type="number" />
                                        <template #append>
                                            <span>грн.</span>
                                        </template>
                                    </InputGroup>
                                </td>
                                <td class="text-right">
                                    {{ Price.factory(orderProduct.seller_sum).toCurrencyString('UAH') }}
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="6" class="text-right">{{ $t('table.titles.total') }}:</th>
                                <th class="text-right">
                                    {{
                                        Price.factory(orderProducts.reduce((total, orderProduct) => total + (orderProduct.amount * orderProduct.price), 0)).toCurrencyString('UAH')
                                    }}
                                </th>
                                <th colspan="2"></th>
                                <th class="text-right">
                                    {{
                                        Price.factory(orderProducts.reduce((total, orderProduct) => total + (orderProduct.seller_sum || 0), 0)).toCurrencyString('UAH')
                                    }}
                                </th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </template>
        </div>
        <div class="modal-footer" v-if="!fetchTask.state.isRunning && !createTask.state.isRunning">
            <AppButton color="indigo" :disabled="!allProductsHasSeller" @click="createExpense">{{ $t('buttons.create') }}</AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../../components/new/button/AppButton.vue';
import TaskProgressBar from '../../../../../components/new/progress/TaskProgressBar.vue';
import AppNullableCell from '../../../../components/tables/AppNullableCell.vue';
import AppAutoComplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import Input from '../../../../../components/new/form/Input.vue';
import InputGroup from '../../../../../components/new/form/InputGroup.vue';
import Price from '../../../../../services/Price/Price';

import { httpClient } from '../../../../../services/HttpClient';
import { createProgressTask } from '../../../../../services/Progress/ProgressTask';
import { PLACEHOLDER_IMAGE_URL } from '../../../../constants';

export default {
    name: "CreateProductPurchaseExpenseModal",
    components: {
        AppModal, AppButton, TaskProgressBar, AppNullableCell, AppAutoComplete, Input,
        InputGroup
    },
    emits: ['update'],
    data() {
        return {
            Price,
            fetchTask: createProgressTask(),
            createTask: createProgressTask(),
            orderProducts: []
        };
    },
    methods: {
        show(orderProductUuids) {
            this.$refs.modal.show();
            this.fetchOrderProducts(orderProductUuids);
        },
        hide() {
            this.fetchTask.cancel();
            this.createTask.cancel();
            this.$refs.modal.hide();
            this.orderProducts = [];
        },
        fetchOrderProducts(orderProductUuids) {
            return this.fetchTask.run(async ({signal}) => {
                await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/products`, {
                    uuids: orderProductUuids,
                    extra_fields: ['product'],
                    orders: [{ column: 'id', dir: 'desc' }],
                    limit: 100
                }).then((response) => {
                    if (signal.aborted) {
                        return;
                    }

                    this.orderProducts = response.data.data.map(product => ({ ...product, seller: null, seller_price: 0, seller_sum: 0 }));
                })
            });
        },
        selectDropshipper(orderProduct, seller, sellerPrice) {
            orderProduct.seller = seller;
            orderProduct.seller_price = sellerPrice;
            this.updateSellerSum(orderProduct);
        },
        updateSellerSum(orderProduct) {
            orderProduct.seller_sum = orderProduct.amount * orderProduct.seller_price;
        },
        async createExpense() {
            const completed = await this.createTask.run(async () => {
                await httpClient.post(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/expenses`, {
                    name: "Закупка в постачальника",
                    sum: this.orderProducts.reduce((total, orderProduct) => total + (orderProduct.seller_sum || 0), 0),
                    order_uuid: this.$route.params.orderUuid,
                    type: 'products_cost'
                });
            });

            if (completed) {
                this.$emit('update');
                this.hide();
            }
        }
    },
    computed: {
        allProductsHasSeller() {
            return !this.orderProducts.some(orderProduct => !orderProduct.seller);
        }
    },
    beforeUnmount() {
        this.fetchTask.cancel();
        this.createTask.cancel();
    }
}
</script>
<style scoped>
.table-image {
    width: 38px;
    height: 38px;
}
</style>